// /pack/:packId/color/:itemId — paint-bucket coloring on a Gemini-produced
// lineart, with R2 autosave so a kid can close the tab and resume later.
//
// Flow on mount:
//   1. POST /api/artworks {packId, itemId} — server returns putUrl +
//      thumbnailPutUrl, plus resumeGetUrl when an in-progress row already
//      exists. The frontend never round-trips bytes through the Worker.
//   2. Load lineart from /assets/... (already pipeline-binarized) and, on
//      resume, the user's saved PNG over the top — that PNG already
//      contains the lineart, so the flood-fill engine sees the same edges.
//   3. Bind ColoringEngine, run a ripple-reveal fill animation per click
//      identical to the playground (proven UX).
//   4. Debounce 2s after each fill; PUT R2 with the current canvas + PUT
//      /api/artworks/:id with fillActionCount. Failed PUTs retry up to 3x
//      with backoff and silently move on; we don't bug the kid mid-paint.
//   5. visibilitychange/pagehide flush — best effort save before navigate.
//
// Step 9 task 21 boundary: completion (thumbnail upload + fly-to-scene
// animation + 4-note arpeggio) lives in the next task — the "完成" button
// here just navigates back for now.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
} from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ColoringEngine,
  GENERAL_PALETTE,
  JOSEPH_PALETTE,
  NATIVITY_PALETTE,
  type FillCandidate,
  type RGB,
  type Swatch,
} from '@skena/coloring'
import {
  assetUrl,
  bonusAssetUrl,
  PACKS,
  type PackBonusResponse,
  type PackId,
} from '@skena/schema'
import { artworksApi, ArtworksApiError, putR2 } from '../scene/artworksApi.ts'
import { fetchPackBonus, fetchPackScene } from '../scene/sceneApi.ts'
import { fireCapstoneConfetti } from '../practice/confetti.ts'
import { capstoneFlourish, playArpeggio } from '../practice/sounds.ts'
import { ShareButton } from '../components/ShareButton.tsx'
import { DownloadButton } from '../components/DownloadButton.tsx'
import { useAuthStore } from '../auth/store.ts'
import { CapstoneReveal } from '../coloring/CapstoneReveal.tsx'
import { useColoringTarget } from './useColoringTarget.ts'

const CANVAS_W = 1408
const CANVAS_H = 786
const FILL_DURATION_MS = 150
const UNDO_FADE_MS = 200
const AUTOSAVE_DEBOUNCE_MS = 2000
const AUTOSAVE_MAX_RETRIES = 3
// Brush radius in canvas pixels. Canvas displays ~344px wide on phone
// (4:1 down-scale from 1408), so a 40-canvas-px brush ≈ 10 screen-px —
// Brush radius is a continuous slider, matching the original ColoringPlayground
// prototype 主公 remembered. Range 1..10 (canvas px). Small radii are needed
// for tucking color into thin gaps the bucket can't reach; the larger end
// stays modest because brush mode is for detail work, not bulk fill.
const MIN_BRUSH_RADIUS = 1
const MAX_BRUSH_RADIUS = 10
const DEFAULT_BRUSH_RADIUS = 3
// Maximum gap between consecutive pointer samples we'll bridge by stamping.
// 1/2 of the active brush radius keeps strokes continuous without doubling
// cost on a fast drag. Floor at 1 so very thin brushes still produce a
// continuous line on fast drags.
const brushStampSpacing = (radius: number) => Math.max(1, radius / 2)

type Tool = 'bucket' | 'brush'

const PACK_PALETTE: Record<PackId, readonly Swatch[]> = {
  joseph: JOSEPH_PALETTE,
  nativity: NATIVITY_PALETTE,
}

const PACK_BG: Record<PackId, string> = {
  joseph: 'bg-[var(--color-joseph-bg)]',
  nativity: 'bg-[var(--color-nativity-bg)]',
}
const PACK_BUTTON: Record<PackId, string> = {
  joseph:
    'bg-[var(--color-joseph-primary)] text-white shadow-md hover:-translate-y-0.5 hover:shadow-lg',
  nativity:
    'bg-[var(--color-nativity-primary)] text-white shadow-md hover:-translate-y-0.5 hover:shadow-lg',
}

interface OnLineMarker {
  id: number
  x: number
  y: number
}

interface OpenedArtwork {
  artworkId: string
  putUrl: string
  thumbnailPutUrl: string
  urlsExpireAt: number
  resumeGetUrl: string | null
  fillActionCount: number
}

export function ColoringPage() {
  const target = useColoringTarget()
  if (target.mode === 'invalid' || !target.packId) {
    return <Navigate to="/" replace />
  }
  if (target.mode === 'bonus-redirect-needed') {
    return <Navigate to={`/pack/${target.packId}/bonus`} replace />
  }
  if (target.mode === 'bonus') {
    return (
      <BonusColoringGate
        packId={target.packId}
        artworkItemId={target.artworkItemId!}
        lineartUrl={target.lineartUrl!}
      />
    )
  }
  // mode === 'item'
  return (
    <ColoringInner
      mode="item"
      packId={target.packId}
      artworkItemId={target.artworkItemId!}
      lineartUrl={target.lineartUrl!}
      bonusMeta={null}
    />
  )
}

// Mount-time guard for /pack/:packId/bonus. Locked → redirect home with a
// toast hint. Unlocked → mount ColoringInner in bonus mode. We use the same
// pack-bonus query the PackScene CapstoneSection uses so cache invalidation
// after capstone unlock (task 3.6) cascades here too.
function BonusColoringGate({
  packId,
  artworkItemId,
  lineartUrl,
}: {
  packId: PackId
  artworkItemId: string
  lineartUrl: string
}) {
  const bonusQuery = useQuery({
    queryKey: ['pack-bonus', packId],
    queryFn: () => fetchPackBonus(packId),
    staleTime: 30 * 1000,
  })

  if (bonusQuery.isLoading || bonusQuery.isFetching && !bonusQuery.data) {
    return (
      <main className={`mx-auto max-w-md p-8 text-center ${PACK_BG[packId]}`}>
        <p className="text-[var(--color-muted)]">準備中…</p>
      </main>
    )
  }
  if (bonusQuery.isError || !bonusQuery.data) {
    return <Navigate to={`/pack/${packId}`} replace />
  }
  if (!bonusQuery.data.isUnlocked) {
    // Deeplink to bonus while locked: send them home. v0.1 alpha is small
    // enough that a single toast on PackHome covers explanation; we don't
    // build a dedicated "locked" page.
    return <Navigate to={`/pack/${packId}?bonusLocked=1`} replace />
  }

  return (
    <ColoringInner
      mode="bonus"
      packId={packId}
      artworkItemId={artworkItemId}
      lineartUrl={lineartUrl}
      bonusMeta={bonusQuery.data}
    />
  )
}

interface ColoringInnerProps {
  mode: 'item' | 'bonus'
  packId: PackId
  artworkItemId: string
  lineartUrl: string
  /** Present in bonus mode — drives share-card metadata + capstone branding. */
  bonusMeta: PackBonusResponse | null
}

function ColoringInner({ mode, packId, artworkItemId, lineartUrl, bonusMeta }: ColoringInnerProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const packPalette = PACK_PALETTE[packId]
  const pack = PACKS[packId]

  // Validate itemId belongs to this pack — defends a typo'd manual URL.
  // Bonus mode skips this check; the BonusColoringGate already vouched.
  const itemValid =
    mode === 'bonus' || (pack.itemIds as readonly string[]).includes(artworkItemId)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const tempCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const engineRef = useRef<ColoringEngine | null>(null)
  const animatingRef = useRef(false)
  // Active brush stroke state — refs because pointermove fires too often
  // for setState. lastBrushPosRef tracks the last canvas-pixel coord we
  // stamped at; used to interpolate when the cursor jumps.
  const drawingRef = useRef(false)
  const lastBrushPosRef = useRef<{ x: number; y: number } | null>(null)

  // Mutable autosave state — refs so they don't trigger re-renders mid-paint.
  const artworkRef = useRef<OpenedArtwork | null>(null)
  // Reference colored.jpg loaded as ImageData for the "魔法補色 ✨" action.
  // Null until the off-screen load resolves; null forever in bonus mode (no colored
  // reference exists). Button is disabled while null.
  const coloredImageDataRef = useRef<ImageData | null>(null)
  const saveTimerRef = useRef<number | null>(null)
  const saveInFlightRef = useRef<Promise<void> | null>(null)
  const dirtyRef = useRef(false)
  const lastSyncedFillCountRef = useRef(0)

  // Palette state: track the active hex string so selection survives tab switches
  // between "主題色" (pack palette) and "更多色" (general palette). Active hex
  // initializes to the pack's first swatch.
  const [activeHex, setActiveHex] = useState<string>(packPalette[0]!.hex)
  const [paletteTab, setPaletteTab] = useState<'pack' | 'general'>('pack')
  const palette = paletteTab === 'pack' ? packPalette : GENERAL_PALETTE
  const [fillCount, setFillCount] = useState(0)
  // True once the reference colored.jpg has been loaded and decoded into
  // coloredImageDataRef. Drives both "魔法補色 ✨" and "看原圖 👁" buttons.
  const [magicReady, setMagicReady] = useState(false)
  // "看原圖 👁" peek state — true while the user holds the button down.
  // Renders the reference colored.jpg as an absolute overlay on top of the
  // canvas so they can compare without losing their own work.
  const [peeking, setPeeking] = useState(false)
  const [onLineMarker, setOnLineMarker] = useState<OnLineMarker | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [phase, setPhase] = useState<
    | 'loading'
    | 'ready'
    | 'error'
    | 'completing'
    | 'shareable'
    | 'capstone-revealing'
    | 'capstone-cta'
  >('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [tool, setTool] = useState<Tool>('bucket')
  const [brushRadiusPx, setBrushRadiusPx] = useState<number>(DEFAULT_BRUSH_RADIUS)

  // activeColor is derived from activeHex; falls back to the pack's first swatch
  // if activeHex somehow doesn't decode (shouldn't happen — all swatches use
  // 6-char hex). We look up rgb across BOTH palettes so the active color follows
  // the user across tab switches even when the swatch isn't visible in the
  // currently-shown palette row.
  const activeColor: RGB =
    [...packPalette, ...GENERAL_PALETTE].find((sw) => sw.hex === activeHex)?.rgb ??
    packPalette[0]!.rgb

  // Pull display name + church name + scene metadata for the post-complete
  // share card. Lazy: not awaited at mount, only consumed when phase flips
  // to 'shareable'. Bonus mode skips the scene fetch — its metadata comes
  // from the PackBonusResponse instead.
  const user = useAuthStore((s) => s.user)
  const sceneQuery = useQuery({
    queryKey: ['pack-scene', packId],
    queryFn: () => fetchPackScene(packId),
    staleTime: 30 * 1000,
    enabled: mode === 'item',
  })
  const sceneItem =
    mode === 'item' ? sceneQuery.data?.items.find((i) => i.itemId === artworkItemId) : undefined

  // ---------- Mount: open + load + bind ----------

  useEffect(() => {
    if (!itemValid) {
      setPhase('error')
      setErrorMessage('這個元素不屬於這個 pack')
      return
    }
    let cancelled = false

    const canvas = canvasRef.current
    if (!canvas) return
    canvas.width = CANVAS_W
    canvas.height = CANVAS_H
    const temp = document.createElement('canvas')
    temp.width = CANVAS_W
    temp.height = CANVAS_H
    tempCanvasRef.current = temp
    const ctx = canvas.getContext('2d')!

    const engine = new ColoringEngine(canvas, { devChecks: import.meta.env.DEV })
    engineRef.current = engine

    void (async () => {
      try {
        const opened = await artworksApi.open(packId, artworkItemId)
        if (cancelled) return
        artworkRef.current = {
          artworkId: opened.artworkId,
          putUrl: opened.putUrl,
          thumbnailPutUrl: opened.thumbnailPutUrl,
          urlsExpireAt: opened.urlsExpireAt,
          resumeGetUrl: opened.resumeGetUrl,
          fillActionCount: opened.fillActionCount,
        }
        lastSyncedFillCountRef.current = opened.fillActionCount

        // Always paint the lineart first — gives the engine a known
        // black/white substrate. On resume, the user PNG overlays it.
        await drawImageOnCanvas(ctx, lineartUrl)
        if (opened.isResume && opened.resumeGetUrl) {
          try {
            await drawImageOnCanvas(ctx, opened.resumeGetUrl)
          } catch (err) {
            // Resume PNG fetch failed — fall back to a fresh canvas. The
            // server still considers the row in-progress, so future saves
            // overwrite the bad PNG. Don't bug the kid about it.
            console.warn('[coloring] resume PNG load failed; starting fresh', err)
          }
        }
        if (cancelled) return
        engine.loadImageData(ctx.getImageData(0, 0, CANVAS_W, CANVAS_H))
        setFillCount(engine.fillCount())
        setPhase('ready')
      } catch (err) {
        if (cancelled) return
        const msg = err instanceof ArtworksApiError ? err.message : '載入失敗，等等再回來'
        setErrorMessage(msg)
        setPhase('error')
      }
    })()

    return () => {
      cancelled = true
    }
  }, [packId, artworkItemId, itemValid, lineartUrl])

  // ---------- Load reference colored.jpg for "魔法補色 ✨" ----------
  // Both item AND bonus modes load the colored reference — items use
  // assetUrl(packId, itemId, 'colored'), bonus uses bonusAssetUrl(bonusId,
  // 'colored') because the bonus URL shape differs (snake_case bonus id,
  // /bonus/ path segment). The decoded ImageData parks in coloredImageDataRef
  // and unlocks the "魔法補色 ✨" + "看原圖 👁" buttons.
  useEffect(() => {
    let cancelled = false
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (cancelled) return
      try {
        const off = document.createElement('canvas')
        off.width = CANVAS_W
        off.height = CANVAS_H
        const offCtx = off.getContext('2d', { willReadFrequently: true })
        if (!offCtx) return
        offCtx.drawImage(img, 0, 0, CANVAS_W, CANVAS_H)
        coloredImageDataRef.current = offCtx.getImageData(0, 0, CANVAS_W, CANVAS_H)
        setMagicReady(true)
      } catch (err) {
        console.warn('[coloring] colored reference decode failed', err)
      }
    }
    img.onerror = () => {
      if (!cancelled) console.warn('[coloring] colored reference load failed')
    }
    img.src =
      mode === 'bonus'
        ? bonusAssetUrl(artworkItemId, 'colored')
        : assetUrl(packId, artworkItemId, 'colored')
    return () => {
      cancelled = true
    }
  }, [mode, packId, artworkItemId])

  // ---------- Autosave plumbing ----------

  const flushSave = useCallback(async (): Promise<void> => {
    const canvas = canvasRef.current
    const engine = engineRef.current
    const art = artworkRef.current
    if (!canvas || !engine || !art) return
    if (!dirtyRef.current) return
    dirtyRef.current = false
    const fillCount = engine.fillCount()

    const blob = await canvasToBlob(canvas, 'image/png')
    if (!blob) return

    let putUrl = art.putUrl
    if (Date.now() > art.urlsExpireAt - 30_000) {
      try {
        const refreshed = await artworksApi.autosave(art.artworkId, fillCount)
        art.putUrl = refreshed.putUrl
        art.thumbnailPutUrl = refreshed.thumbnailPutUrl
        art.urlsExpireAt = refreshed.urlsExpireAt
        putUrl = refreshed.putUrl
        lastSyncedFillCountRef.current = fillCount
      } catch (err) {
        console.warn('[coloring] re-sign failed; will retry next tick', err)
        // Keep the dirty flag so a later save tries again.
        dirtyRef.current = true
        return
      }
    }

    let attempt = 0
    while (attempt < AUTOSAVE_MAX_RETRIES) {
      try {
        await putR2(putUrl, blob)
        break
      } catch (err) {
        attempt++
        if (attempt >= AUTOSAVE_MAX_RETRIES) {
          console.warn('[coloring] R2 PUT giving up after retries', err)
          dirtyRef.current = true
          return
        }
        await new Promise((r) => setTimeout(r, 400 * 2 ** attempt))
      }
    }

    // Bump metadata occasionally so server's fillActionCount stays warm —
    // doesn't have to match every fill, just close enough for analytics.
    if (fillCount - lastSyncedFillCountRef.current >= 5) {
      try {
        const refreshed = await artworksApi.autosave(art.artworkId, fillCount)
        art.putUrl = refreshed.putUrl
        art.thumbnailPutUrl = refreshed.thumbnailPutUrl
        art.urlsExpireAt = refreshed.urlsExpireAt
        lastSyncedFillCountRef.current = fillCount
      } catch (err) {
        console.warn('[coloring] metadata bump failed', err)
      }
    }
  }, [])

  const scheduleSave = useCallback(() => {
    dirtyRef.current = true
    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current)
    }
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null
      const p = flushSave().finally(() => {
        if (saveInFlightRef.current === p) saveInFlightRef.current = null
      })
      saveInFlightRef.current = p
    }, AUTOSAVE_DEBOUNCE_MS)
  }, [flushSave])

  // Best-effort flush on tab hide / unload. Browsers don't await async work
  // here, but the resulting fetches usually fly out before the page dies.
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        if (saveTimerRef.current !== null) {
          window.clearTimeout(saveTimerRef.current)
          saveTimerRef.current = null
        }
        void flushSave()
      }
    }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', onHide)
    }
  }, [flushSave])

  // ---------- Interaction ----------

  const showToast = useCallback((msg: string) => {
    setToast(msg)
    const t = window.setTimeout(() => setToast(null), 1400)
    return () => window.clearTimeout(t)
  }, [])

  // Convert pointer event to canvas-pixel coords, accounting for CSS scaling.
  const canvasCoords = (
    e: PointerEvent<HTMLCanvasElement>,
    canvas: HTMLCanvasElement,
  ): { x: number; y: number; cssX: number; cssY: number } => {
    const rect = canvas.getBoundingClientRect()
    const cssX = e.clientX - rect.left
    const cssY = e.clientY - rect.top
    return {
      x: (cssX * canvas.width) / rect.width,
      y: (cssY * canvas.height) / rect.height,
      cssX,
      cssY,
    }
  }

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLCanvasElement>) => {
      const engine = engineRef.current
      const canvas = canvasRef.current
      if (!engine || !canvas || animatingRef.current || phase !== 'ready') return
      const { x, y, cssX, cssY } = canvasCoords(e, canvas)

      if (tool === 'bucket') {
        const { result, candidate } = engine.prepareFill(x, y, activeColor)
        if (!candidate) {
          if (result.status === 'on-line') {
            setOnLineMarker({ id: Date.now(), x: cssX, y: cssY })
            showToast('點顏色區塊內喔')
            window.setTimeout(() => setOnLineMarker(null), 600)
          } else if (result.status === 'too-small') {
            showToast(`太小的縫填不到（${result.filledPixelCount} px）`)
          } else if (result.status === 'too-large') {
            showToast('這格線條沒封住——切到 🖌️ 筆刷塗看看')
          }
          return
        }
        animatingRef.current = true
        animateRippleReveal(canvas, tempCanvasRef.current!, candidate, FILL_DURATION_MS)
          .then(() => {
            candidate.commit()
            setFillCount(engine.fillCount())
            scheduleSave()
          })
          .catch((err: unknown) => {
            console.error('[coloring] ripple animation failed', err)
            candidate.cancel()
          })
          .finally(() => {
            animatingRef.current = false
          })
        return
      }

      // brush mode — start a stroke. startBrushStroke computes a home-region
      // mask 4-connected from (x, y) with lineart as walls, so the whole
      // stroke is locked to one region — dragging past a line stops painting
      // on the other side. (主公 round-1: "還是可以亂畫".)
      drawingRef.current = true
      lastBrushPosRef.current = { x, y }
      canvas.setPointerCapture(e.pointerId)
      engine.startBrushStroke(x, y)
      engine.brushStamp(x, y, brushRadiusPx, activeColor)
    },
    [activeColor, scheduleSave, showToast, phase, tool, brushRadiusPx],
  )

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLCanvasElement>) => {
      if (tool !== 'brush' || !drawingRef.current) return
      const engine = engineRef.current
      const canvas = canvasRef.current
      if (!engine || !canvas) return
      const { x, y } = canvasCoords(e, canvas)
      const last = lastBrushPosRef.current
      if (!last) {
        engine.brushStamp(x, y, brushRadiusPx, activeColor)
        lastBrushPosRef.current = { x, y }
        return
      }
      // Bridge gaps with intermediate stamps so a fast drag still paints
      // a continuous stroke instead of a dotted trail.
      const dx = x - last.x
      const dy = y - last.y
      const dist = Math.hypot(dx, dy)
      if (dist < 1) return
      const steps = Math.max(1, Math.ceil(dist / brushStampSpacing(brushRadiusPx)))
      for (let i = 1; i <= steps; i++) {
        const t = i / steps
        engine.brushStamp(last.x + dx * t, last.y + dy * t, brushRadiusPx, activeColor)
      }
      lastBrushPosRef.current = { x, y }
    },
    [activeColor, tool, brushRadiusPx],
  )

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLCanvasElement>) => {
      if (tool !== 'brush' || !drawingRef.current) return
      const engine = engineRef.current
      const canvas = canvasRef.current
      if (!engine || !canvas) return
      drawingRef.current = false
      lastBrushPosRef.current = null
      try {
        canvas.releasePointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      // Whole stroke = one history step.
      engine.commitBrushStroke()
      setFillCount(engine.fillCount())
      scheduleSave()
    },
    [scheduleSave, tool],
  )

  const onUndo = useCallback(() => {
    const engine = engineRef.current
    const canvas = canvasRef.current
    if (!engine || !canvas) return
    if (!engine.canUndo()) return
    const ctx = canvas.getContext('2d')!
    const before = ctx.getImageData(0, 0, canvas.width, canvas.height)
    engine.undo()
    const after = ctx.getImageData(0, 0, canvas.width, canvas.height)
    void fadeBetween(canvas, tempCanvasRef.current!, before, after, UNDO_FADE_MS).then(() => {
      setFillCount(engine.fillCount())
      scheduleSave()
    })
  }, [scheduleSave])

  // "重畫" — rewind to history[0]. For a fresh paint that's the pristine
  // lineart; for a resumed / already-completed artwork it's the colored
  // state we loaded so the kid can re-edit from "完成" again. Use 清空 for
  // a true reset all the way to lineart regardless of resume state.
  const onReset = useCallback(() => {
    const engine = engineRef.current
    if (!engine) return
    if (!window.confirm('要回到一開始載入時的樣子嗎？')) return
    engine.resetToInitial()
    setFillCount(engine.fillCount())
    scheduleSave()
  }, [scheduleSave])

  // "清空" — wipe to white, redraw the lineart asset, reload engine. Always
  // ends at the pristine lineart even when the artwork was loaded from a
  // resumed/completed state. Round-1 dogfood: "希望的『清空』是真正恢復到
  // 線條稿".
  const onWipe = useCallback(async () => {
    const engine = engineRef.current
    const canvas = canvasRef.current
    if (!engine || !canvas) return
    if (!window.confirm('要把所有顏色清掉，回到原本的線條稿嗎？')) return
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    try {
      await drawImageOnCanvas(ctx, lineartUrl)
    } catch (err) {
      console.warn('[coloring] wipe lineart redraw failed', err)
      return
    }
    engine.loadImageData(ctx.getImageData(0, 0, canvas.width, canvas.height))
    setFillCount(engine.fillCount())
    scheduleSave()
  }, [lineartUrl, scheduleSave])

  // "魔法補色 ✨" — sample colored.jpg for every unfilled region and fill in one
  // undoable batch. Does NOT mark the artwork complete or touch XP/streak (round-1
  // feedback #3). Disabled if the colored reference failed to load (button is
  // wrapped with disabled state in the JSX below).
  const onMagicAutofill = useCallback(() => {
    const engine = engineRef.current
    const colored = coloredImageDataRef.current
    if (!engine || !colored) return
    if (phase !== 'ready') return
    const t0 = performance.now()
    const result = engine.magicAutofill(colored)
    const t1 = performance.now()
    if (result.filled.length === 0) {
      // Either nothing to fill (already fully colored) or every region was
      // skipped due to invalid samples. Either way show the same celebratory
      // toast — F5 in design (button stays enabled because "all colored" is a
      // positive end state, not a failure).
      showToast('都上色完啦 🎉')
      return
    }
    if (typeof console !== 'undefined') {
      console.log(`[coloring] magicAutofill: ${result.filled.length} filled, ${result.skipped.length} skipped, ${(t1 - t0).toFixed(0)}ms`)
    }
    setFillCount(engine.fillCount())
    scheduleSave()
  }, [phase, scheduleSave, showToast])

  // ---------- 完成: thumbnail → upload → mark complete → celebrate → navigate

  const onComplete = useCallback(async () => {
    const canvas = canvasRef.current
    const engine = engineRef.current
    const art = artworkRef.current
    if (!canvas || !engine || !art) return
    setPhase('completing')

    if (saveTimerRef.current !== null) {
      window.clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }

    try {
      // 1. Final flush of the main PNG. flushSave only reads dirtyRef, so
      //    force-mark dirty even if the last fill was already in flight.
      dirtyRef.current = true
      await flushSave()

      // 2. Generate + upload thumbnail (300×164 JPG q85).
      const thumbBlob = await generateThumbnail(canvas)

      // Re-sign URLs if expired so both the final PNG and thumbnail land.
      let putUrl = art.putUrl
      let thumbnailPutUrl = art.thumbnailPutUrl
      if (Date.now() > art.urlsExpireAt - 30_000) {
        const refreshed = await artworksApi.autosave(art.artworkId, engine.fillCount())
        art.putUrl = refreshed.putUrl
        art.thumbnailPutUrl = refreshed.thumbnailPutUrl
        art.urlsExpireAt = refreshed.urlsExpireAt
        putUrl = refreshed.putUrl
        thumbnailPutUrl = refreshed.thumbnailPutUrl
      }
      void putUrl // already used in flushSave

      await putR2(thumbnailPutUrl, thumbBlob)

      // 3. Mark complete server-side. Response tells us whether this was
      //    the moment the pack's capstone unlocked.
      const completeRes = await artworksApi.complete(art.artworkId, engine.fillCount())

      // Invalidate cached pack state for BOTH branches so PackScene shows
      // the freshly-completed cell when the kid navigates back. Without
      // this, the kid sees stale "unlocked-pending" until they hard-refresh.
      // pack-bonus also gets invalidated so CapstoneSection re-evaluates
      // (cheap; usually no-op for non-capstone completes).
      void queryClient.invalidateQueries({ queryKey: ['pack-scene', packId] })
      void queryClient.invalidateQueries({ queryKey: ['pack-bonus', packId] })

      // 4. Branch on bonusJustUnlocked:
      //    • true  → capstone reveal (rare, momentous)
      //    • false → regular shareable overlay (item complete OR bonus
      //              artwork complete OR re-completion of last item)
      if (completeRes.bonusJustUnlocked) {
        setPhase('capstone-revealing')
        capstoneFlourish()
        fireCapstoneConfetti(packId)
        // Phase 2 fades the CTAs in after the hero has settled.
        window.setTimeout(() => {
          setPhase((p) => (p === 'capstone-revealing' ? 'capstone-cta' : p))
        }, 1000)
      } else {
        playArpeggio()
        // Show shareable overlay — kid picks "share" or "later". Both
        // branches eventually navigate, but we wait for their choice
        // instead of hard-cutting.
        setPhase('shareable')
      }
    } catch (err) {
      console.error('[coloring] complete failed', err)
      setPhase('ready')
      setToast('完成失敗，等等再試一次')
    }
  }, [flushSave, navigate, packId, artworkItemId, queryClient])

  // ---------- Render ----------

  // Bonus mode pulls naming from the bonus metadata; item mode keeps the
  // scene-query lookup with the artworkItemId fallback for first-render.
  const itemNameZh =
    mode === 'bonus' ? bonusMeta?.nameZh ?? pack.bonus.nameZh : sceneItem?.nameZh ?? artworkItemId
  const itemNameEn = mode === 'bonus' ? bonusMeta?.nameEn ?? pack.bonus.nameEn : sceneItem?.nameEn ?? ''
  const scriptureRef =
    mode === 'bonus'
      ? bonusMeta?.scriptureRef ?? pack.bonus.scriptureRef
      : sceneItem?.scriptureRef ?? ''
  const descriptionZh =
    mode === 'bonus'
      ? // Bonus has no descriptionZh in metadata; use the pack-level recap so
        // the share card still carries a line of context instead of empty space.
        `蒐齊整個 ${pack.nameZh}，留下整套的紀念。`
      : sceneItem?.descriptionZh ?? ''

  if (!itemValid) {
    return (
      <main className={`mx-auto max-w-md p-8 text-center ${PACK_BG[packId]}`}>
        <p className="text-[var(--color-muted)]">這個元素不屬於 {pack.nameZh}。</p>
        <Link to={`/pack/${packId}`} className="mt-4 inline-block underline">
          回 {pack.nameZh}
        </Link>
      </main>
    )
  }

  if (phase === 'error') {
    const backTo = mode === 'bonus' ? `/pack/${packId}` : `/pack/${packId}/inventory`
    const backLabel = mode === 'bonus' ? `回 ${pack.nameZh}` : '回我的線條稿'
    return (
      <main className={`mx-auto max-w-md p-8 text-center ${PACK_BG[packId]}`}>
        <p className="text-[var(--color-muted)]">{errorMessage}</p>
        <Link to={backTo} className="mt-4 inline-block underline">
          {backLabel}
        </Link>
      </main>
    )
  }

  return (
    <main
      className={`mx-auto flex min-h-full max-w-3xl flex-col gap-4 p-4 ${PACK_BG[packId]}`}
      data-pack={packId}
    >
      <header className="flex items-baseline justify-between gap-3">
        <Link
          to={mode === 'bonus' ? `/pack/${packId}` : `/pack/${packId}/inventory`}
          className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
        >
          ← 退出
        </Link>
        <p className="text-xs text-[var(--color-muted)]">
          {phase === 'loading' ? '準備中…' : `填了 ${fillCount} 次 · ${itemNameZh}`}
        </p>
      </header>

      <ColoringActionsToolbar
        mode={mode}
        packId={packId}
        artworkItemId={artworkItemId}
        itemNameZh={itemNameZh}
        itemNameEn={itemNameEn}
        scriptureRef={scriptureRef}
        descriptionZh={descriptionZh}
        sceneItemComplete={sceneItem?.isComplete ?? false}
        sceneItemArtworkUrl={sceneItem?.artworkUrl ?? null}
        sceneItemArtworkId={sceneItem?.artworkId ?? null}
        bonusMeta={bonusMeta}
        displayName={user?.displayName ?? '小朋友'}
        churchName={user?.churchName ?? null}
        getHero={() => canvasToHeroImage(canvasRef.current)}
      />

      <div className="relative mx-auto w-full max-w-3xl">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="block w-full touch-none rounded-[var(--radius-soft)] border border-[var(--color-muted)]/20 bg-white shadow-sm select-none"
          style={{ aspectRatio: '1408 / 786', cursor: phase === 'ready' ? 'crosshair' : 'wait' }}
          aria-label="上色畫布 — 點擊填色"
        />
        {peeking && magicReady ? (
          <img
            src={
              mode === 'bonus'
                ? bonusAssetUrl(artworkItemId, 'colored')
                : assetUrl(packId, artworkItemId, 'colored')
            }
            alt="原本彩圖"
            className="pointer-events-none absolute inset-0 block w-full rounded-[var(--radius-soft)]"
            style={{ aspectRatio: '1408 / 786' }}
            aria-hidden="true"
          />
        ) : null}
        <AnimatePresence>
          {onLineMarker && (
            <motion.div
              key={onLineMarker.id}
              initial={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
              animate={{
                opacity: [0, 1, 1, 0],
                scale: [0.8, 1.15, 1, 0.95],
                x: ['-52%', '-48%', '-52%', '-50%'],
                y: '-50%',
              }}
              transition={{ duration: 0.55, times: [0, 0.2, 0.7, 1] }}
              exit={{ opacity: 0 }}
              className="pointer-events-none absolute size-7 rounded-full border-2 border-[var(--color-joseph-primary)]"
              style={{ left: onLineMarker.x, top: onLineMarker.y }}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>
      </div>

      <ToolToggle tool={tool} onChange={setTool} packId={packId} />
      {tool === 'brush' ? (
        <BrushSizeSlider value={brushRadiusPx} onChange={setBrushRadiusPx} />
      ) : null}

      <PaletteTabs tab={paletteTab} onChange={setPaletteTab} />
      <Palette
        palette={palette}
        activeHex={activeHex}
        onPick={(sw) => setActiveHex(sw.hex)}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onUndo}
            className="rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 px-4 py-2 disabled:opacity-40"
            disabled={phase !== 'ready'}
          >
            ↶ 上一步
          </button>
          <button
            onClick={onReset}
            className="rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 px-4 py-2 text-[var(--color-muted)] disabled:opacity-40"
            disabled={phase !== 'ready' || fillCount === 0}
            title="回到一開始載入時的樣子"
          >
            重畫
          </button>
          <button
            onClick={onWipe}
            className="rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 px-4 py-2 text-[var(--color-muted)] disabled:opacity-40"
            disabled={phase !== 'ready'}
            title="去除所有顏色，回到原本的線條稿"
          >
            清空
          </button>
          <button
            onClick={onMagicAutofill}
            className="rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 px-4 py-2 disabled:opacity-40"
            disabled={phase !== 'ready' || !magicReady}
            aria-disabled={!magicReady}
            title={magicReady ? '魔法補色' : '需要原圖才能補色'}
          >
            魔法補色 ✨
          </button>
          <button
            onPointerDown={(e) => {
              e.preventDefault()
              setPeeking(true)
            }}
            onPointerUp={() => setPeeking(false)}
            onPointerLeave={() => setPeeking(false)}
            onPointerCancel={() => setPeeking(false)}
            className="rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 px-4 py-2 disabled:opacity-40"
            disabled={!magicReady}
            aria-disabled={!magicReady}
            title={magicReady ? '按住看原本彩圖' : '需要原圖'}
          >
            看原圖 👁
          </button>
        </div>
        <button
          onClick={onComplete}
          disabled={phase !== 'ready' || fillCount < 1}
          className={`rounded-[var(--radius-pill)] px-6 py-2 text-base font-semibold disabled:opacity-40 ${PACK_BUTTON[packId]}`}
        >
          完成
        </button>
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none fixed top-6 left-1/2 -translate-x-1/2 rounded-[var(--radius-pill)] bg-[var(--color-fg)] px-4 py-2 text-sm text-white shadow"
            role="status"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(phase === 'completing' || phase === 'shareable') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-white/85 backdrop-blur-sm"
            aria-live="assertive"
          >
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: [0.7, 1.08, 1], opacity: 1 }}
              transition={{ duration: 0.55, times: [0, 0.7, 1], ease: 'easeOut' }}
              className="flex flex-col items-center gap-4 px-6 text-center"
            >
              <p className="font-[family-name:var(--font-display)] text-3xl text-[var(--color-fg)]">
                完成了！
              </p>
              {phase === 'completing' ? (
                <p className="text-sm text-[var(--color-muted)]">把彩圖送回故事畫面…</p>
              ) : (
                <ShareableActions
                  packId={packId}
                  artworkId={artworkRef.current?.artworkId ?? ''}
                  itemNameZh={itemNameZh}
                  itemNameEn={itemNameEn}
                  scriptureRef={scriptureRef}
                  descriptionZh={descriptionZh}
                  displayName={user?.displayName ?? '小朋友'}
                  churchName={user?.churchName ?? null}
                  getHero={() => canvasToHeroImage(canvasRef.current)}
                  isCapstone={mode === 'bonus'}
                  onContinue={() =>
                    navigate(
                      mode === 'bonus'
                        ? `/pack/${packId}`
                        : `/pack/${packId}?justCompleted=${artworkItemId}`,
                    )
                  }
                />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {(phase === 'capstone-revealing' || phase === 'capstone-cta') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-white/92 backdrop-blur-sm"
          >
            <CapstoneReveal
              packId={packId}
              phase={phase}
              itemsCollected={pack.itemIds.length}
              onColorNow={() => navigate(`/pack/${packId}/bonus`)}
              onLater={() => navigate(`/pack/${packId}`)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  )
}

function BrushSizeSlider({
  value,
  onChange,
}: {
  value: number
  onChange: (next: number) => void
}) {
  // Visual dot scales with the current radius so kids can see what they've
  // picked at a glance. The slider value IS the canvas-pixel radius — fine
  // detail (1) up to chunky (10).
  return (
    <div className="flex items-center justify-center gap-3">
      <span className="text-xs text-[var(--color-muted)]">細</span>
      <input
        type="range"
        min={MIN_BRUSH_RADIUS}
        max={MAX_BRUSH_RADIUS}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-40 max-w-[60vw] accent-[var(--color-fg)]"
        aria-label="筆刷粗細"
      />
      <span className="text-xs text-[var(--color-muted)]">粗</span>
      <span
        aria-hidden="true"
        className="block rounded-full bg-[var(--color-fg)]"
        style={{ width: Math.max(4, value * 2), height: Math.max(4, value * 2) }}
      />
    </div>
  )
}

function ToolToggle({
  tool,
  onChange,
  packId,
}: {
  tool: Tool
  onChange: (t: Tool) => void
  packId: PackId
}) {
  const activeRing =
    packId === 'joseph'
      ? 'ring-2 ring-[var(--color-joseph-primary)]'
      : 'ring-2 ring-[var(--color-nativity-primary)]'
  return (
    <div
      className="flex items-center justify-center gap-2"
      role="radiogroup"
      aria-label="繪圖工具"
    >
      <button
        type="button"
        onClick={() => onChange('bucket')}
        aria-checked={tool === 'bucket'}
        role="radio"
        className={`rounded-[var(--radius-pill)] bg-white px-4 py-2 text-sm shadow-sm ${
          tool === 'bucket' ? activeRing : 'opacity-60'
        }`}
      >
        🪣 填色
      </button>
      <button
        type="button"
        onClick={() => onChange('brush')}
        aria-checked={tool === 'brush'}
        role="radio"
        className={`rounded-[var(--radius-pill)] bg-white px-4 py-2 text-sm shadow-sm ${
          tool === 'brush' ? activeRing : 'opacity-60'
        }`}
      >
        🖌️ 筆刷
      </button>
    </div>
  )
}

function PaletteTabs({
  tab,
  onChange,
}: {
  tab: 'pack' | 'general'
  onChange: (next: 'pack' | 'general') => void
}) {
  const baseBtn =
    'min-h-[44px] rounded-[var(--radius-pill)] px-5 py-2 text-sm font-medium transition-colors'
  return (
    <div
      role="tablist"
      aria-label="色盤切換"
      className="flex items-center justify-center gap-2"
    >
      <button
        role="tab"
        aria-selected={tab === 'pack'}
        onClick={() => onChange('pack')}
        className={`${baseBtn} ${
          tab === 'pack'
            ? 'bg-[var(--color-fg)] text-white'
            : 'border border-[var(--color-muted)]/30 text-[var(--color-muted)]'
        }`}
      >
        主題色
      </button>
      <button
        role="tab"
        aria-selected={tab === 'general'}
        onClick={() => onChange('general')}
        className={`${baseBtn} ${
          tab === 'general'
            ? 'bg-[var(--color-fg)] text-white'
            : 'border border-[var(--color-muted)]/30 text-[var(--color-muted)]'
        }`}
      >
        更多色
      </button>
    </div>
  )
}

function Palette({
  palette,
  activeHex,
  onPick,
}: {
  palette: readonly Swatch[]
  activeHex: string
  onPick: (sw: Swatch) => void
}) {
  return (
    <div className="flex flex-wrap justify-center gap-2" role="radiogroup" aria-label="palette">
      {palette.map((sw) => {
        const isActive = sw.hex === activeHex
        return (
          <motion.button
            key={sw.hex}
            onClick={() => onPick(sw)}
            whileTap={{ scale: 0.92 }}
            animate={isActive ? { scale: 1.05 } : { scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className={`relative size-12 rounded-full ${
              isActive
                ? 'ring-2 ring-[var(--color-fg)] ring-offset-2 ring-offset-[var(--color-bg)]'
                : 'ring-1 ring-black/10'
            }`}
            style={{ backgroundColor: sw.hex }}
            aria-label={sw.nameZh}
            aria-checked={isActive}
            role="radio"
          />
        )
      })}
    </div>
  )
}

// ---------- helpers ----------

function drawImageOnCanvas(ctx: CanvasRenderingContext2D, url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new window.Image()
    // crossOrigin needed because R2 presigned URLs are cross-origin to localhost.
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      ctx.drawImage(img, 0, 0, CANVAS_W, CANVAS_H)
      resolve()
    }
    img.onerror = () => reject(new Error(`image load failed: ${url}`))
    img.src = url
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type)
  })
}

/**
 * 300×164 JPG q85 thumbnail. White background since JPG can't carry alpha
 * and the user's canvas may have white-ish negative space we don't want
 * showing as black after compositing.
 */
async function generateThumbnail(canvas: HTMLCanvasElement): Promise<Blob> {
  const tmp = document.createElement('canvas')
  tmp.width = 300
  tmp.height = 164
  const tctx = tmp.getContext('2d')!
  tctx.fillStyle = '#ffffff'
  tctx.fillRect(0, 0, tmp.width, tmp.height)
  tctx.drawImage(canvas, 0, 0, tmp.width, tmp.height)
  return new Promise((resolve, reject) => {
    tmp.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('thumbnail toBlob failed'))
      },
      'image/jpeg',
      0.85,
    )
  })
}

function animateRippleReveal(
  canvas: HTMLCanvasElement,
  tempCanvas: HTMLCanvasElement,
  candidate: FillCandidate,
  durationMs: number,
): Promise<void> {
  const ctx = canvas.getContext('2d')!
  const tempCtx = tempCanvas.getContext('2d')!
  tempCtx.putImageData(candidate.after, 0, 0)
  const radius = Math.max(0, (candidate.result.maxDistance ?? 0) + 3)

  return new Promise((resolve, reject) => {
    let start = -1
    function frame(now: number) {
      if (start < 0) start = now
      const t = Math.max(0, Math.min(1, (now - start) / durationMs))
      const eased = 1 - Math.pow(1 - t, 2)
      const r = Math.max(0, radius * eased)
      try {
        ctx.putImageData(candidate.before, 0, 0)
        ctx.save()
        ctx.beginPath()
        ctx.arc(candidate.startX, candidate.startY, r, 0, Math.PI * 2)
        ctx.clip()
        ctx.drawImage(tempCanvas, 0, 0)
        ctx.restore()
      } catch (err) {
        reject(err)
        return
      }
      if (t < 1) requestAnimationFrame(frame)
      else resolve()
    }
    requestAnimationFrame(frame)
  })
}

function fadeBetween(
  canvas: HTMLCanvasElement,
  tempCanvas: HTMLCanvasElement,
  before: ImageData,
  after: ImageData,
  durationMs: number,
): Promise<void> {
  const ctx = canvas.getContext('2d')!
  const tempCtx = tempCanvas.getContext('2d')!
  tempCtx.putImageData(after, 0, 0)
  return new Promise((resolve) => {
    let start = -1
    function frame(now: number) {
      if (start < 0) start = now
      const t = Math.max(0, Math.min(1, (now - start) / durationMs))
      ctx.putImageData(before, 0, 0)
      ctx.save()
      ctx.globalAlpha = t
      ctx.drawImage(tempCanvas, 0, 0)
      ctx.restore()
      if (t < 1) requestAnimationFrame(frame)
      else {
        ctx.globalAlpha = 1
        ctx.putImageData(after, 0, 0)
        resolve()
      }
    }
    requestAnimationFrame(frame)
  })
}

// Wrap the live canvas as an HTMLImageElement so the share-card composer
// (which expects HTMLImageElement) can draw it without re-fetching from R2.
async function canvasToHeroImage(canvas: HTMLCanvasElement | null): Promise<HTMLImageElement> {
  if (!canvas) throw new Error('no canvas')
  const blob = await canvasToBlob(canvas, 'image/png')
  if (!blob) throw new Error('canvas toBlob returned null')
  const url = URL.createObjectURL(blob)
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('hero image load failed'))
    img.src = url
  })
}

// Shareable overlay actions: ShareButton + 「稍後」, both lead to PackScene.
// isCapstone flips the share card layout to add the "全景大獎" badge — see
// share-card-canvas.ts. Capstone-driven copy ("分享給家人") stays the same;
// only the rendered artifact differs.
function ShareableActions({
  packId,
  artworkId,
  itemNameZh,
  itemNameEn,
  scriptureRef,
  descriptionZh,
  displayName,
  churchName,
  getHero,
  isCapstone,
  onContinue,
}: {
  packId: PackId
  artworkId: string
  itemNameZh: string
  itemNameEn: string
  scriptureRef: string
  descriptionZh: string
  displayName: string
  churchName: string | null
  getHero: () => Promise<HTMLImageElement>
  isCapstone: boolean
  onContinue: () => void
}) {
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-[var(--color-muted)]">想分享給家人嗎？</p>
      <ShareButton
        artworkId={artworkId}
        getHero={getHero}
        itemNameZh={itemNameZh}
        itemNameEn={itemNameEn}
        scriptureRef={scriptureRef}
        descriptionZh={descriptionZh}
        displayName={displayName}
        churchName={churchName}
        packId={packId}
        isCapstone={isCapstone}
        onShared={onContinue}
      />
      <button
        type="button"
        onClick={onContinue}
        className="text-sm text-[var(--color-muted)] underline-offset-4 hover:underline"
      >
        稍後
      </button>
    </div>
  )
}

// Header toolbar: lineart PDF / artwork PNG / share. Three pill buttons in
// a wrap row. lineart PDF is always enabled; PNG + share require the
// artwork to be complete (so an artworkUrl exists in scene/bonus state).
function ColoringActionsToolbar({
  mode,
  packId,
  artworkItemId,
  itemNameZh,
  itemNameEn,
  scriptureRef,
  descriptionZh,
  sceneItemComplete,
  sceneItemArtworkUrl,
  sceneItemArtworkId,
  bonusMeta,
  displayName,
  churchName,
  getHero,
}: {
  mode: 'item' | 'bonus'
  packId: PackId
  artworkItemId: string
  itemNameZh: string
  itemNameEn: string
  scriptureRef: string
  descriptionZh: string
  sceneItemComplete: boolean
  sceneItemArtworkUrl: string | null
  sceneItemArtworkId: string | null
  bonusMeta: PackBonusResponse | null
  displayName: string
  churchName: string | null
  getHero: () => Promise<HTMLImageElement>
}) {
  const isComplete =
    mode === 'bonus' ? bonusMeta?.isComplete === true : sceneItemComplete
  const artworkUrl = mode === 'bonus' ? bonusMeta?.artworkUrl ?? null : sceneItemArtworkUrl
  const artworkId =
    mode === 'bonus' ? bonusMeta?.artworkId ?? null : sceneItemArtworkId
  const isBonus = mode === 'bonus'

  return (
    <section className="-mt-1 flex flex-wrap items-center justify-center gap-2">
      <DownloadButton
        layout="pill"
        variant="lineart"
        packId={packId}
        itemId={artworkItemId}
        isBonus={isBonus}
        pdfMeta={{ nameZh: itemNameZh, nameEn: itemNameEn, scriptureRef }}
        label="下載著色圖"
        ariaLabel={`下載 ${itemNameZh} 著色圖 PDF`}
      />
      {isComplete && artworkUrl ? (
        <DownloadButton
          layout="pill"
          variant="png"
          packId={packId}
          itemId={artworkItemId}
          isBonus={isBonus}
          artworkUrl={artworkUrl}
          pngWatermark={{ displayName }}
          label="下載手繪圖"
          ariaLabel={`下載 ${itemNameZh} 手繪圖 PNG`}
        />
      ) : (
        <span
          aria-disabled="true"
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-muted)] opacity-60"
        >
          ⬇ 下載手繪圖
        </span>
      )}
      {artworkId && isComplete ? (
        <ShareButton
          artworkId={artworkId}
          getHero={getHero}
          itemNameZh={itemNameZh}
          itemNameEn={itemNameEn}
          scriptureRef={scriptureRef}
          descriptionZh={descriptionZh}
          displayName={displayName}
          churchName={churchName}
          packId={packId}
          isCapstone={isBonus}
          variant="primary"
        />
      ) : (
        <span
          aria-disabled="true"
          className="inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] border border-[var(--color-muted)]/30 bg-white px-3 py-1.5 text-xs font-medium text-[var(--color-muted)] opacity-60"
        >
          ❦ 分享給家人
        </span>
      )}
    </section>
  )
}
